import { _getLogger } from '@doxyme/analytics';
import { getAppBasename } from '../../../hooks/useAppRouter/getAppBasename';
import { isProduction } from '../../../shared/isProduction';
import { store } from '../../../stores/redux/store';
import {
  getSideloadParamsFromHashFragment,
  parseSideloadParam,
  SIDELOAD_PARAM_KEY,
} from './parseSideloadParam';
import { processSideloadExtension } from './processSideloadExtension';
import { setSideloadBaseUrl } from './sideloadBaseUrlRegistry';
import { syncSideloadedExtensionsToMembers } from './syncSideloadedExtensionsToMembers';

const logger = _getLogger('applySideload', {
  component: 'extensions',
});

export const PENDING_SIDELOAD_PARAMS_KEY = 'extensions_pendingSideloadParams';

export type ApplySideloadResult =
  | {
      status: 'success';
      name: string;
    }
  | {
      status: 'parse_error';
      param: string;
    }
  | {
      status: 'process_error';
      manifestName: string;
    };

export type SideloadPrepareResult = {
  params: string[];
  cleanAsPath: string;
};

const splitAsPath = (
  asPath: string,
): { path: string; query: string; hash: string } => {
  const hashIndex = asPath.indexOf('#');
  const pathAndQuery = hashIndex >= 0 ? asPath.slice(0, hashIndex) : asPath;
  const hash = hashIndex >= 0 ? asPath.slice(hashIndex) : '';
  const queryIndex = pathAndQuery.indexOf('?');
  const path =
    queryIndex >= 0 ? pathAndQuery.slice(0, queryIndex) : pathAndQuery;
  const query = queryIndex >= 0 ? pathAndQuery.slice(queryIndex + 1) : '';
  return { path, query, hash };
};

const removeSideloadKeysFromSearchParams = (
  searchParams: URLSearchParams,
): void => {
  while (searchParams.has(SIDELOAD_PARAM_KEY)) {
    searchParams.delete(SIDELOAD_PARAM_KEY);
  }
};

const readSideloadParamsFromAsPath = (asPath: string): string[] => {
  const { query, hash } = splitAsPath(asPath);
  const hashParams = getSideloadParamsFromHashFragment(hash);
  if (hashParams.length > 0) {
    return hashParams;
  }
  return new URLSearchParams(query).getAll(SIDELOAD_PARAM_KEY);
};

const buildCleanAsPath = (asPath: string): string => {
  const { path, query, hash } = splitAsPath(asPath);

  const queryParams = new URLSearchParams(query);
  removeSideloadKeysFromSearchParams(queryParams);
  const queryString = queryParams.toString();
  let result = queryString ? `${path}?${queryString}` : path;

  if (!hash) {
    return result;
  }

  if (getSideloadParamsFromHashFragment(hash).length === 0) {
    return `${result}${hash}`;
  }

  const hashBody = hash.startsWith('#') ? hash.slice(1) : hash;
  const strippedHashParams = new URLSearchParams(hashBody);
  removeSideloadKeysFromSearchParams(strippedHashParams);
  const hashString = strippedHashParams.toString();
  return hashString ? `${result}#${hashString}` : result;
};

/**
 * Parses sideload params from a static-site-router asPath and the path to navigate
 * to afterward. Returns null when sideloading is disabled or the URL has no sideload.
 * Only removes `sideloadExtension`; other query/hash keys (e.g. `upgrade`) are kept.
 */
export const prepareSideloadFromAsPath = (
  asPath: string,
): SideloadPrepareResult | null => {
  if (isProduction()) {
    return null;
  }

  const params = readSideloadParamsFromAsPath(asPath);
  if (params.length === 0) {
    return null;
  }

  return {
    params,
    cleanAsPath: buildCleanAsPath(asPath),
  };
};

const stashPendingSideloadParams = (params: string[]): void => {
  if (typeof sessionStorage === 'undefined' || params.length === 0) {
    return;
  }
  sessionStorage.setItem(PENDING_SIDELOAD_PARAMS_KEY, JSON.stringify(params));
};

const takePendingSideloadParams = (): string[] => {
  if (typeof sessionStorage === 'undefined') {
    return [];
  }
  const raw = sessionStorage.getItem(PENDING_SIDELOAD_PARAMS_KEY);
  if (!raw) {
    return [];
  }
  sessionStorage.removeItem(PENDING_SIDELOAD_PARAMS_KEY);
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      logger.warn('[takePendingSideloadParams] Ignoring invalid stash shape');
      return [];
    }
    return parsed.filter((item): item is string => typeof item === 'string');
  } catch {
    logger.warn(
      '[takePendingSideloadParams] Failed to parse stashed sideload params',
    );
    return [];
  }
};

const resolveNavigationTarget = (asPath: string): string => {
  const basePath = getAppBasename();
  return asPath.startsWith(basePath) ? asPath : `${basePath}${asPath}`;
};

/**
 * Stashes sideload params and reloads to the clean path.
 *
 * Uses a full navigation instead of `router.push` because the initial HTML is
 * always `static-site-router` while the browser URL already shows the target
 * path (and often a hash). Next.js can treat `router.push` to that path as a
 * shallow/hash-only update and never mount the real page. Verified locally via
 * `node e2e/v2/scripts/investigate-sideload-routing.mjs` (Jun 2026).
 */
export const redirectAfterSideloadPrepare = (
  prepared: SideloadPrepareResult,
): void => {
  stashPendingSideloadParams(prepared.params);
  window.location.replace(resolveNavigationTarget(prepared.cleanAsPath));
};

export const applySideloadParams = (
  sideloadParams: string[],
): ApplySideloadResult[] => {
  if (isProduction()) {
    logger.warn('[applySideloadParams] Sideloading is disabled on production');
    return [];
  }

  const results: ApplySideloadResult[] = [];

  for (const param of sideloadParams) {
    const parsed = parseSideloadParam(param);
    if (!parsed) {
      logger.error('[applySideloadParams] Failed to parse sideload param', {
        param,
      });
      results.push({
        status: 'parse_error',
        param,
      });
      continue;
    }

    try {
      const result = processSideloadExtension({
        baseUrl: parsed.baseUrl,
        manifest: parsed.manifest,
      });
      setSideloadBaseUrl(parsed.manifest.name, parsed.baseUrl);
      void store.dispatch(syncSideloadedExtensionsToMembers());
      results.push({
        status: 'success',
        name: result.name,
      });
    } catch (error) {
      logger.error('[applySideloadParams] Failed to process sideload', {
        error,
        manifestName: parsed.manifest.name,
      });
      results.push({
        status: 'process_error',
        manifestName: parsed.manifest.name,
      });
    }
  }

  return results;
};

/**
 * Applies sideload params stashed by static-site-router (consumes the stash).
 */
export const applyPendingSideloadParams = (): ApplySideloadResult[] => {
  return applySideloadParams(takePendingSideloadParams());
};
